import { useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import { ConfirmationDialog } from 'components';
import { TEAM_ADMIN, TEAM_BASIC } from 'settings/constants/roles';
import { AgentStatus } from 'types';
import { showSuccessMessage } from 'helpers/success';
import {
  requestGetSymphonyListEffect,
  updateClientStatusEffect,
  updateMemberActiveStatusEffect,
} from 'store/effects';
import { AGENT_CONNECTION_STATUSES } from 'settings/constants/common';
import { Select } from 'components';
import { AGENT_TYPE } from 'settings/constants/drawers';
import { getAgentTypeSelector, getUserId } from 'store/selectors/user';
import { useOutsideClick } from 'hooks';
import { postCancelInviteClientById, postReinviteClientById } from 'api/clientsList';
import styles from './styles.module.scss';
import { requestActiveSubscriptionTeamDataEffect } from 'store/effects/subscription';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { postReinviteTeamMember } from 'api/teamList';

export const Status = ({ contact, isAgent }) => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const agentType = useSelector(getAgentTypeSelector);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const dispatch = useDispatch();
  const buttonRef = useRef(null);
  const optionsRef = useRef(null);
  const lastInviteRef = useRef<any>(null);
  const userId = useSelector(getUserId);
  const [invited, setInvited] = useState(contact?.SendInvitation);
  const isPending = isAgent
    ? contact.InvitationStatus === AGENT_CONNECTION_STATUSES.PENDING
    : contact.Client?.InvitationStatus === AGENT_CONNECTION_STATUSES.PENDING;
  const limitedAccess = userId === contact.Id && agentRole === TEAM_BASIC;

  useOutsideClick([buttonRef, optionsRef], () => setOpen(false));

  const onStatusChange = (obj) => {
    const isContactActive = isAgent ? contact.IsActive : !contact?.Deactivated;
    if (
      (obj.name === AgentStatus.Inactive && isContactActive) ||
      (obj.name === AgentStatus.Active && !isContactActive)
    )
      setOpenConfirmModal(true);
  };

  const changeClientStatus = () => {
    setSaving(true);
    dispatch(
      updateClientStatusEffect(
        {
          status: !contact?.Deactivated,
          id: contact.Id,
        },
        undefined,
        (err, resp) => {
          if (!err) {
            dispatch(requestGetSymphonyListEffect({}, { silent: true }));
            dispatch(requestActiveSubscriptionTeamDataEffect({}, { showError: false }));
            showSuccessMessage(`Client ${!contact?.Deactivated ? 'Deactivated' : 'Activated'}.`);
          }
          setSaving(false);
        },
      ),
    );
  };

  const changeAgentStatus = async () => {
    setSaving(true);
    dispatch(
      updateMemberActiveStatusEffect(
        { isActive: !contact.IsActive, silent: true },
        { agentId: contact.Id },
        (err) => {
          if (!err) {
            dispatch(requestGetSymphonyListEffect({}, { silent: true }));
            dispatch(requestActiveSubscriptionTeamDataEffect({}, { showError: false }));
            showSuccessMessage(`Agent ${contact.IsActive ? 'deactivated' : 'activated'}.`);
            setSaving(false);
          }
        },
      ),
    );
  };

  const onSubmitConfirmModal = () => {
    setOpenConfirmModal(false);
    isAgent ? changeAgentStatus() : changeClientStatus();
  };

  const changeUserStatusContent = useMemo(() => {
    const isActive = isAgent ? contact?.IsActive : !contact?.Deactivated;

    return contact ? (
      <div className={styles.userStatusConfirmText}>
        <h1>{isActive ? 'Deactivate' : 'Activate'} Account</h1>
        <p>
          {contact?.FirstName} will {isActive ? 'no longer have' : 'regain'} access to this account.
        </p>
      </div>
    ) : null;
  }, [contact]);

  const resendInvite = async () => {
    if (loading) return;
    setLoading(true);

    const response = isAgent
      ? await postReinviteTeamMember({ id: contact.Id, useExistingToken: true })
      : await postReinviteClientById({ id: contact.Id });
    if (response.status === 200) {
      updateLastInviteDate(response?.data?.lastInvitedAt);
      showSuccessMessage(invited ? 'Resent invite' : 'Invite sent');
      if (!invited) setInvited(true);
    }
    setLoading(false);
  };

  const updateLastInviteDate = (lastInvitedAt) => {
    if (!lastInvitedAt || !lastInviteRef?.current) return;

    const updatedDateTime = `Last Invite: ${moment(lastInvitedAt).format(
      'MM/DD/YYYY [at] hh:mm A',
    )}`;
    lastInviteRef.current.textContent = updatedDateTime;
  };

  const cancelInvite = async () => {
    if (loading) return;
    setLoading(true);
    const result = await postCancelInviteClientById({ id: contact.Id });
    if (result?.data?.success) showSuccessMessage('Invite Canceled');
    setLoading(false);
  };

  const renderPendingStatus = () => (
    <div
      className={styles.pendingContainer}
      ref={buttonRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div className={styles.top}>
        <div className={styles.pendingStatus}>Pending</div>
        <div className={styles.arrowIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3.50012 6L8.00012 10.5L12.5001 6"
              stroke="#747475"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      {invited && (
        <div className={styles.lastInvite} ref={lastInviteRef}>
          {`Last Invite: ${
            contact.InviteSentAt
              ? moment(contact.InviteSentAt).format('MM/DD/YYYY [at] hh:mm A')
              : '-'
          }`}
        </div>
      )}
      {open && (
        <div ref={optionsRef} className={styles.options}>
          <ul>
            {invited ? (
              <li testid="resend_action" className={styles.item} onClick={resendInvite}>
                <div className={styles.icon}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3.0201 9.03009C2.92223 10.1718 3.18343 11.3156 3.7672 12.3017C4.35097 13.2878 5.22816 14.0669 6.27624 14.5302C7.32432 14.9935 8.49094 15.1179 9.61314 14.886C10.7353 14.6541 11.7571 14.0774 12.5356 13.2366C13.3142 12.3958 13.8107 11.3328 13.9558 10.1961C14.1008 9.05936 13.8872 7.90573 13.3448 6.89633C12.8024 5.88692 11.9582 5.07215 10.9302 4.56582C9 3.61512 6 4 6 4M6 4L9 1M6 4L9 6.89633"
                      stroke="#515151"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Resend</span>
              </li>
            ) : (
              <li testid="resend_action" className={styles.item} onClick={resendInvite}>
                <div className={styles.icon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.084 2.9165L11.7622 17.6538C11.7263 17.7322 11.6686 17.7986 11.596 17.8452C11.5235 17.8917 11.439 17.9165 11.3528 17.9165C11.2666 17.9165 11.1822 17.8917 11.1096 17.8452C11.037 17.7986 10.9794 17.7322 10.9434 17.6538L8.99472 11.0057L2.34669 9.05704C2.26831 9.02112 2.20188 8.96345 2.15531 8.89088C2.10874 8.81831 2.08398 8.7339 2.08398 8.64767C2.08398 8.56145 2.10874 8.47703 2.15531 8.40446C2.20188 8.3319 2.26831 8.27422 2.34669 8.2383L17.084 2.9165Z"
                      stroke="#515151"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Send Invite</span>
              </li>
            )}
            {isAgent && invited ? (
              <li testid="cancel_action" className={styles.item} onClick={copyInviteLink}>
                <div className={styles.icon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_77494_71250)">
                      <path
                        d="M8.86393 3.3335L14.47 3.3335C14.912 3.3335 15.3359 3.50909 15.6485 3.82165C15.9611 4.13421 16.1367 4.55814 16.1367 5.00016V12.5002C16.1367 12.9422 15.9611 13.3661 15.6485 13.6787C15.3359 13.9912 14.912 14.1668 14.47 14.1668H8.86393C8.4219 14.1668 7.99798 13.9912 7.68542 13.6787C7.37286 13.3661 7.19727 12.9422 7.19727 12.5002V5.00016C7.19727 4.55814 7.37286 4.13421 7.68542 3.82165C7.99798 3.50909 8.4219 3.3335 8.86393 3.3335Z"
                        stroke="#515151"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12.8027 14.1665V15.8332C12.8027 16.2752 12.6271 16.6991 12.3145 17.0117C12.002 17.3242 11.578 17.4998 11.136 17.4998H5.52995C5.08792 17.4998 4.664 17.3242 4.35144 17.0117C4.03888 16.6991 3.86328 16.2752 3.86328 15.8332V8.33317C3.86328 7.89114 4.03888 7.46722 4.35144 7.15466C4.664 6.8421 5.08792 6.6665 5.52995 6.6665H7.19661"
                        stroke="#515151"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_77494_71250">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <span>Copy Invite Link</span>
              </li>
            ) : invited ? (
              <li testid="cancel_action" className={styles.item} onClick={cancelInvite}>
                <div className={styles.icon}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M2.99927 13L13.0003 2.99893"
                      stroke="#FF576D"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M13.0007 13L2.99966 2.99893"
                      stroke="#FF576D"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span>Cancel Invite</span>
              </li>
            ) : null}
          </ul>
        </div>
      )}
    </div>
  );

  const getContactStatus = () => {
    return isAgent
      ? contact?.IsActive
        ? 'Active'
        : 'Inactive'
      : contact?.Deactivated
      ? 'Inactive'
      : 'Active';
  };

  const copyInviteLink = () => {
    if (!contact?.InviteLink) return;
    navigator.clipboard.writeText(contact.InviteLink);
    showSuccessMessage('Invitation link copied.');
  };

  const editable = isAgent
    ? agentType === AGENT_TYPE.Team &&
      [TEAM_ADMIN, TEAM_BASIC].includes(contact?.TeamRole) &&
      (contact.Id === userId || contact.IsManagedByCurrentUser)
    : true;

  const value = getContactStatus();

  if (!editable) return <div className={styles.value}>{isPending ? 'Pending' : value || '-'}</div>;

  return (
    <>
      {isPending ? (
        renderPendingStatus()
      ) : (
        <div className={classNames(styles.item, styles.statusItem, styles.expanded)}>
          <Select
            value={{ value, name: value }}
            onSelect={(e, value) => onStatusChange(value)}
            options={[
              { value: AgentStatus.Active, name: AgentStatus.Active },
              { value: AgentStatus.Inactive, name: AgentStatus.Inactive },
            ]}
            className={{
              wrapper: styles.statusWrapper,
            }}
            arrowIconClassName={classNames(styles.arrowIcon, {
              [styles.arrowIconHidden]: limitedAccess,
            })}
            disabled={limitedAccess || saving}
          />
        </div>
      )}
      <ConfirmationDialog
        onReject={() => setOpenConfirmModal(false)}
        onConfirm={onSubmitConfirmModal}
        isOpen={openConfirmModal}
        confirmText="Continue"
      >
        {changeUserStatusContent}
      </ConfirmationDialog>
    </>
  );
};
